import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth, API, authFetch } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight, Zap, FileSpreadsheet, Mail, CheckCircle, Loader2, Lock, User } from "lucide-react";
import { toast } from "sonner";

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/tableau";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    setIsLoading(true);
    try {
      const response = await authFetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password })
      });

      if (response.ok) {
        const userData = await response.json();
        // Store session token in localStorage for Bearer auth
        if (userData.session_token) {
          localStorage.setItem("session_token", userData.session_token);
        }
        login(userData);
        toast.success("Connexion réussie");
        navigate("/tableau");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur de connexion");
      }
    } catch (error) {
      toast.error("Erreur de connexion");
    } finally {
      setIsLoading(false);
    }
  };

  // If user is already logged in, redirect to tableau
  if (user) {
    return <Navigate to="/tableau" replace />;
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Login Modal */}
      {showEmailLogin && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="p-6 border-b border-zinc-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-orange-500 rounded-md flex items-center justify-center">
                    <Zap className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-xl font-bold text-zinc-950 font-['Manrope']">Connexion</span>
                </div>
                <button 
                  onClick={() => setShowEmailLogin(false)}
                  className="text-zinc-400 hover:text-zinc-600"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Email/Password Form */}
              <form onSubmit={handleEmailLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="votre@email.com"
                      className="pl-10"
                      data-testid="email-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Mot de passe</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-10"
                      data-testid="password-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3"
                  data-testid="email-login-btn"
                >
                  {isLoading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Connexion...</>
                  ) : (
                    "Se connecter"
                  )}
                </Button>
              </form>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-zinc-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-zinc-500">ou</span>
                </div>
              </div>

              {/* Google Login */}
              <Button
                type="button"
                variant="outline"
                onClick={handleGoogleLogin}
                className="w-full gap-2 py-3"
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continuer avec Google
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-orange-500 rounded-md flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-zinc-950 font-['Manrope']">OrderFlow</span>
            </div>
            <Button 
              onClick={() => setShowEmailLogin(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white font-semibold"
              data-testid="login-btn"
            >
              Se connecter
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-16">
        {/* Background */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ 
            backgroundImage: "url('https://images.unsplash.com/photo-1491183846256-33aec7637311?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwYWJzdHJhY3QlMjBnZW9tZXRyeSUyMHdoaXRlJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzE5MjAyMzl8MA&ixlib=rb-4.1.0&q=85')" 
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-50/95 via-zinc-50/90 to-zinc-50/80" />
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Column - Content */}
            <div className="animate-slide-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-medium mb-8">
                <Zap className="w-4 h-4" />
                Automatisation IA
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-zinc-950 font-['Manrope'] mb-6">
                Transformez vos emails en{" "}
                <span className="text-orange-500">commandes structurées</span>
              </h1>
              
              <p className="text-lg text-zinc-600 leading-relaxed mb-10 max-w-xl">
                Copiez-collez vos emails de commande. L'IA extrait automatiquement les données. 
                Téléchargez en Excel en un clic. Gagnez des heures chaque jour.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => setShowEmailLogin(true)}
                  className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-6 px-8 text-lg rounded-md shadow-sm active:scale-95"
                  data-testid="hero-cta-btn"
                >
                  Commencer gratuitement
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>

            {/* Right Column - Visual */}
            <div className="relative hidden lg:block">
              <div className="bg-white rounded-lg border border-zinc-200 shadow-lg p-6 animate-fade-in">
                {/* Mock Workbench */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Raw Input */}
                  <div className="bg-zinc-50 rounded-md p-4 border border-zinc-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Mail className="w-4 h-4 text-zinc-400" />
                      <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Email brut</span>
                    </div>
                    <div className="space-y-2">
                      <div className="h-2 bg-zinc-200 rounded w-full"></div>
                      <div className="h-2 bg-zinc-200 rounded w-4/5"></div>
                      <div className="h-2 bg-zinc-200 rounded w-3/4"></div>
                      <div className="h-2 bg-zinc-200 rounded w-full"></div>
                      <div className="h-2 bg-zinc-200 rounded w-2/3"></div>
                    </div>
                  </div>

                  {/* Extracted Data */}
                  <div className="bg-white rounded-md p-4 border border-zinc-200">
                    <div className="flex items-center gap-2 mb-3">
                      <FileSpreadsheet className="w-4 h-4 text-orange-500" />
                      <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Données extraites</span>
                    </div>
                    <div className="space-y-2 text-xs font-mono">
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Client:</span>
                        <span className="text-zinc-800">Acme Corp</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Produit:</span>
                        <span className="text-zinc-800">Widget Pro</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Qté:</span>
                        <span className="text-zinc-800">150</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Total:</span>
                        <span className="text-orange-500 font-semibold">€4,500</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Action Button */}
                <div className="mt-4 flex justify-center">
                  <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium">
                    <CheckCircle className="w-4 h-4" />
                    Téléchargé en Excel
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-zinc-900 font-['Manrope'] mb-4">
              Comment ça marche
            </h2>
            <p className="text-zinc-600 max-w-2xl mx-auto">
              En trois étapes simples, automatisez le traitement de vos commandes
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="bg-zinc-50 rounded-lg p-8 border border-zinc-200">
              <div className="w-12 h-12 bg-orange-500 text-white rounded-md flex items-center justify-center text-xl font-bold mb-6">
                1
              </div>
              <h3 className="text-xl font-semibold text-zinc-900 font-['Manrope'] mb-3">
                Collez votre email
              </h3>
              <p className="text-zinc-600">
                Copiez le contenu de l'email de commande et collez-le dans l'interface. 
                Aucun format spécifique requis.
              </p>
            </div>

            {/* Step 2 */}
            <div className="bg-zinc-50 rounded-lg p-8 border border-zinc-200">
              <div className="w-12 h-12 bg-orange-500 text-white rounded-md flex items-center justify-center text-xl font-bold mb-6">
                2
              </div>
              <h3 className="text-xl font-semibold text-zinc-900 font-['Manrope'] mb-3">
                L'IA extrait les données
              </h3>
              <p className="text-zinc-600">
                Notre IA analyse l'email et extrait automatiquement client, produits, 
                quantités, prix et plus encore.
              </p>
            </div>

            {/* Step 3 */}
            <div className="bg-zinc-50 rounded-lg p-8 border border-zinc-200">
              <div className="w-12 h-12 bg-orange-500 text-white rounded-md flex items-center justify-center text-xl font-bold mb-6">
                3
              </div>
              <h3 className="text-xl font-semibold text-zinc-900 font-['Manrope'] mb-3">
                Téléchargez en Excel
              </h3>
              <p className="text-zinc-600">
                Vérifiez les données, ajustez si nécessaire, puis téléchargez directement 
                un fichier Excel prêt à l'emploi.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-orange-500 rounded-md flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-white font-['Manrope']">OrderFlow</span>
            </div>
            <p className="text-zinc-500 text-sm">
              © 2026 OrderFlow. Automatisation intelligente des commandes.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
